import { useState, useEffect } from 'react';
import axios from 'axios';
import api from '../api';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const API       = (process.env.REACT_APP_API_URL || 'http://localhost:5000') + '/api/projections';
const ROSCA_API = (process.env.REACT_APP_API_URL || 'http://localhost:5000') + '/api/rosca/cycles';

const INCOME_LABELS = {
  loan_interest: 'Loan Interest',
  subscriptions: 'Annual Subscriptions',
  savings:       'Monthly Savings',
  fines:         'Fines',
  donations:     'Donations'
};
const EXPENDITURE_LABELS = {
  bereavement:    'Bereavement Package',
  member_package: 'Member Annual Package',
  stationery:     'Stationery',
  administration: 'Administration'
};

const getUserRole = () => {
  try { return JSON.parse(atob(localStorage.getItem('token').split('.')[1])).role; }
  catch { return null; }
};

function Projections() {
  const [tab, setTab]                     = useState('dashboard');
  const [budgets, setBudgets]             = useState([]);
  const [cycles, setCycles]               = useState([]);
  const [selectedBudget, setSelectedBudget] = useState(null);
  const [budgetDetail, setBudgetDetail]   = useState(null);
  const [loading, setLoading]             = useState(false);
  const [showNewForm, setShowNewForm]     = useState(false);
  const [newForm, setNewForm]             = useState({ cycle_id: '', financial_year: '', notes: '' });
  const [showApproveForm, setShowApproveForm] = useState(false);
  const [approveForm, setApproveForm]     = useState({ resolution_number: '', resolution_date: '' });
  const [showDonationForm, setShowDonationForm] = useState(false);
  const [donationForm, setDonationForm]   = useState({ donor_name: '', amount: '', donation_date: new Date().toISOString().split('T')[0], description: '' });
  const [donations, setDonations]         = useState([]);
  const [error, setError]                 = useState('');
  const [success, setSuccess]             = useState('');

  const userRole = getUserRole();
  const isMember = userRole === 'member';
  const token    = localStorage.getItem('token');
  const headers  = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchBudgets();
    if (!isMember) axios.get(ROSCA_API, { headers }).then(r => setCycles(r.data)).catch(() => {});
  }, []);

  const fetchBudgets = () => {
    axios.get(API, { headers }).then(r => {
      setBudgets(r.data);
      if (r.data.length) loadBudget(r.data[0].id);
    }).catch(() => setError('Failed to load budgets'));
  };

  const loadBudget = async (id) => {
    setLoading(true); setError('');
    try {
      const res = await axios.get(`${API}/${id}`, { headers });
      setSelectedBudget(id);
      setBudgetDetail(res.data);
      if (!isMember) {
        const don = await axios.get(`${API}/${id}/donations`, { headers });
        setDonations(don.data);
      }
    } catch (err) {
      setError('Failed to load budget: ' + (err.response?.data?.error || err.message));
    }
    setLoading(false);
  };

  const createBudget = async (e) => {
    e.preventDefault(); setError(''); setSuccess('');
    try {
      const res = await axios.post(API, newForm, { headers });
      setSuccess('Budget created in draft');
      setShowNewForm(false);
      setNewForm({ cycle_id: '', financial_year: '', notes: '' });
      fetchBudgets();
      loadBudget(res.data.id);
    } catch (err) { setError(err.response?.data?.error || 'Failed to create budget'); }
  };

  const updateLine = async (lineId, projected_amount) => {
    try {
      await axios.patch(`${API}/lines/${lineId}`, { projected_amount }, { headers });
      loadBudget(selectedBudget);
    } catch (err) { setError(err.response?.data?.error || 'Failed to update'); }
  };

  const approveBudget = async (e) => {
    e.preventDefault(); setError(''); setSuccess('');
    try {
      await axios.post(`${API}/${selectedBudget}/approve`, approveForm, { headers });
      setSuccess('Budget approved and locked');
      setShowApproveForm(false);
      loadBudget(selectedBudget);
    } catch (err) { setError(err.response?.data?.error || 'Failed to approve'); }
  };

  const addDonation = async (e) => {
    e.preventDefault(); setError(''); setSuccess('');
    try {
      await axios.post(`${API}/${selectedBudget}/donations`, donationForm, { headers });
      setSuccess('Donation recorded');
      setShowDonationForm(false);
      setDonationForm({ donor_name: '', amount: '', donation_date: new Date().toISOString().split('T')[0], description: '' });
      loadBudget(selectedBudget);
      const don = await axios.get(`${API}/${selectedBudget}/donations`, { headers });
      setDonations(don.data);
    } catch (err) { setError(err.response?.data?.error || 'Failed to record donation'); }
  };

  const generatePDF = () => {
    if (!budgetDetail) return;
    const { budget, lines, actuals } = budgetDetail;
    const doc = new jsPDF();
    doc.setFillColor(44, 62, 80); doc.rect(0, 0, 210, 28, 'F');
    doc.setTextColor(255, 255, 255); doc.setFontSize(16); doc.setFont('helvetica', 'bold');
    doc.text('KIGEZI ASSOCIATION', 105, 12, { align: 'center' });
    doc.setFontSize(10); doc.setFont('helvetica', 'normal');
    doc.text('Kira Municipality, Uganda | Registered CBO', 105, 20, { align: 'center' });
    doc.setTextColor(0, 0, 0); doc.setFontSize(14); doc.setFont('helvetica', 'bold');
    doc.text('FINANCIAL PROJECTIONS — BUDGET PERFORMANCE REPORT', 105, 40, { align: 'center' });
    doc.setFontSize(10); doc.setFont('helvetica', 'normal');
    doc.text(`Cash Round: ${budget.cycle_name}`, 15, 52);
    doc.text(`Financial Year: ${budget.financial_year}`, 15, 60);
    doc.text(`Status: ${budget.status.toUpperCase()}`, 15, 68);
    if (budget.resolution_number) doc.text(`AGM Resolution: ${budget.resolution_number} — ${budget.resolution_date?.slice(0, 10)}`, 15, 76);
    doc.text(`Report Date: ${new Date().toLocaleDateString('en-GB')}`, 15, 84);
    const incomeLines = lines.filter(l => l.line_type === 'income');
    const expLines    = lines.filter(l => l.line_type === 'expenditure');
    const totalIncomeProjected = incomeLines.reduce((s, l) => s + parseFloat(l.projected_amount), 0);
    const totalIncomeActual    = incomeLines.reduce((s, l) => s + (actuals?.income?.[l.category] || 0), 0);
    const totalExpProjected    = expLines.reduce((s, l) => s + parseFloat(l.projected_amount), 0);
    const totalExpActual       = expLines.reduce((s, l) => s + (actuals?.expenditure?.[l.category] || 0), 0);
    autoTable(doc, {
      startY: 95,
      head: [['INCOME', 'Projected (UGX)', 'Actual (UGX)', 'Variance', '% Achieved']],
      body: incomeLines.map(l => {
        const actual = actuals?.income?.[l.category] || 0;
        const variance = actual - parseFloat(l.projected_amount);
        const pct = l.projected_amount > 0 ? Math.round((actual / parseFloat(l.projected_amount)) * 100) : 0;
        return [INCOME_LABELS[l.category] || l.category, parseFloat(l.projected_amount).toLocaleString(), actual.toLocaleString(), (variance >= 0 ? '+' : '') + variance.toLocaleString(), `${pct}%`];
      }),
      foot: [['TOTAL INCOME', totalIncomeProjected.toLocaleString(), totalIncomeActual.toLocaleString(), ((totalIncomeActual - totalIncomeProjected) >= 0 ? '+' : '') + (totalIncomeActual - totalIncomeProjected).toLocaleString(), '']],
      headStyles: { fillColor: [39, 174, 96] }, footStyles: { fillColor: [44, 62, 80], textColor: 255, fontStyle: 'bold' }, styles: { fontSize: 10 }
    });
    autoTable(doc, {
      startY: doc.lastAutoTable.finalY + 10,
      head: [['EXPENDITURE', 'Projected (UGX)', 'Actual (UGX)', 'Variance (Savings)', '% Used']],
      body: expLines.map(l => {
        const actual = actuals?.expenditure?.[l.category] || 0;
        const variance = parseFloat(l.projected_amount) - actual;
        const pct = l.projected_amount > 0 ? Math.round((actual / parseFloat(l.projected_amount)) * 100) : 0;
        return [EXPENDITURE_LABELS[l.category] || l.category, parseFloat(l.projected_amount).toLocaleString(), actual.toLocaleString(), (variance >= 0 ? '+' : '') + variance.toLocaleString(), `${pct}%`];
      }),
      foot: [['TOTAL EXPENDITURE', totalExpProjected.toLocaleString(), totalExpActual.toLocaleString(), ((totalExpProjected - totalExpActual) >= 0 ? '+' : '') + (totalExpProjected - totalExpActual).toLocaleString(), '']],
      headStyles: { fillColor: [231, 76, 60] }, footStyles: { fillColor: [44, 62, 80], textColor: 255, fontStyle: 'bold' }, styles: { fontSize: 10 }
    });
    const finalY = doc.lastAutoTable.finalY + 15;
    doc.setFontSize(12); doc.setFont('helvetica', 'bold');
    doc.text(`Projected Net Surplus / (Deficit): UGX ${(totalIncomeProjected - totalExpProjected).toLocaleString()}`, 15, finalY);
    doc.text(`Actual Net Surplus / (Deficit): UGX ${(totalIncomeActual - totalExpActual).toLocaleString()}`, 15, finalY + 10);
    if (budget.resolution_number) {
      doc.setFontSize(8); doc.setFont('helvetica', 'normal'); doc.setTextColor(100);
      doc.text(`Approved by AGM Resolution ${budget.resolution_number} on ${budget.resolution_date?.slice(0, 10)}`, 105, 285, { align: 'center' });
    }
    doc.save(`Projections-${budget.cycle_name}-${new Date().toISOString().slice(0, 10)}.pdf`);
  };

  const variance = (projected, actual, type = 'income') => {
    const diff = type === 'income' ? actual - projected : projected - actual;
    const color = diff >= 0 ? '#2ecc71' : '#e74c3c';
    const sign  = diff >= 0 ? '+' : '';
    return <span style={{ color, fontWeight: 'bold' }}>{sign}{diff.toLocaleString()}</span>;
  };

  const progressBar = (actual, projected) => {
    const pct   = projected > 0 ? Math.min(100, Math.round((actual / projected) * 100)) : 0;
    const color = pct >= 100 ? '#2ecc71' : pct >= 60 ? '#f39c12' : '#e74c3c';
    return (
      <div style={{ background: '#ecf0f1', borderRadius: '4px', height: '8px', width: '100%', marginTop: '3px' }}>
        <div style={{ background: color, width: `${pct}%`, height: '8px', borderRadius: '4px' }} />
      </div>
    );
  };

  const b           = budgetDetail?.budget;
  const lines       = budgetDetail?.lines || [];
  const actuals     = budgetDetail?.actuals;
  const incomeLines = lines.filter(l => l.line_type === 'income');
  const expLines    = lines.filter(l => l.line_type === 'expenditure');
  const totalIncomeProjected = incomeLines.reduce((s, l) => s + parseFloat(l.projected_amount), 0);
  const totalIncomeActual    = incomeLines.reduce((s, l) => s + (actuals?.income?.[l.category] || 0), 0);
  const totalExpProjected    = expLines.reduce((s, l) => s + parseFloat(l.projected_amount), 0);
  const totalExpActual       = expLines.reduce((s, l) => s + (actuals?.expenditure?.[l.category] || 0), 0);
  const netProjected = totalIncomeProjected - totalExpProjected;
  const netActual    = totalIncomeActual    - totalExpActual;

  return (
    <div style={{ padding: '20px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '5px' }}>
        <h2 style={{ color: '#2c3e50', margin: 0 }}>Financial Projections</h2>
        <div style={{ display: 'flex', gap: '8px' }}>
          {!isMember && b && b.status === 'draft' && <button onClick={() => setShowApproveForm(true)} style={btnStyle('#27ae60')}>✓ Approve Budget</button>}
          {!isMember && b && <button onClick={() => setShowDonationForm(true)} style={btnStyle('#8e44ad')}>+ Donation</button>}
          {b && <button onClick={generatePDF} style={btnStyle('#e74c3c')}>⬇ PDF Report</button>}
          {!isMember && <button onClick={() => setShowNewForm(true)} style={btnStyle('#2c3e50')}>+ New Budget</button>}
        </div>
      </div>
      <p style={{ color: '#7f8c8d', fontSize: '13px', marginBottom: '20px' }}>
        Cash Round financial year budget — AGM approved, real-time tracking
        {isMember && <span style={{ marginLeft: '10px', color: '#8e44ad', fontWeight: 'bold', fontSize: '11px' }}>● READ-ONLY VIEW</span>}
      </p>

      {error   && <div style={alertStyle('#e74c3c')}>{error}   <span onClick={() => setError('')}   style={{ cursor: 'pointer', float: 'right' }}>✕</span></div>}
      {success && <div style={alertStyle('#2ecc71')}>{success} <span onClick={() => setSuccess('')} style={{ cursor: 'pointer', float: 'right' }}>✕</span></div>}

      {!isMember && showNewForm && (
        <form onSubmit={createBudget} style={formStyle}>
          <h3 style={{ margin: '0 0 15px', color: '#2c3e50', width: '100%' }}>New Budget</h3>
          <div style={fieldStyle}>
            <label style={labelStyle}>Cash Round *</label>
            <select value={newForm.cycle_id} onChange={e => setNewForm({ ...newForm, cycle_id: e.target.value })} style={inputStyle} required>
              <option value="">-- Select --</option>
              {cycles.map(c => <option key={c.id} value={c.id}>{c.cycle_name} ({c.status})</option>)}
            </select>
          </div>
          <div style={fieldStyle}>
            <label style={labelStyle}>Financial Year *</label>
            <input value={newForm.financial_year} onChange={e => setNewForm({ ...newForm, financial_year: e.target.value })} style={inputStyle} placeholder="e.g. 2026-2027" required />
          </div>
          <div style={{ ...fieldStyle, minWidth: '300px' }}>
            <label style={labelStyle}>Notes</label>
            <input value={newForm.notes} onChange={e => setNewForm({ ...newForm, notes: e.target.value })} style={inputStyle} />
          </div>
          <div style={{ width: '100%', display: 'flex', gap: '10px' }}>
            <button type="submit" style={btnStyle('#2c3e50')}>Create Budget</button>
            <button type="button" onClick={() => setShowNewForm(false)} style={btnStyle('#95a5a6')}>Cancel</button>
          </div>
        </form>
      )}

      {!isMember && showApproveForm && (
        <form onSubmit={approveBudget} style={{ ...formStyle, background: '#f0fff4', borderColor: '#27ae60' }}>
          <h3 style={{ margin: '0 0 15px', color: '#27ae60', width: '100%' }}>Approve Budget — AGM Resolution</h3>
          <div style={fieldStyle}>
            <label style={labelStyle}>Resolution Number *</label>
            <input value={approveForm.resolution_number} onChange={e => setApproveForm({ ...approveForm, resolution_number: e.target.value })} style={inputStyle} placeholder="e.g. AGM/2026/001" required />
          </div>
          <div style={fieldStyle}>
            <label style={labelStyle}>Resolution Date *</label>
            <input type="date" value={approveForm.resolution_date} onChange={e => setApproveForm({ ...approveForm, resolution_date: e.target.value })} style={inputStyle} required />
          </div>
          <div style={{ width: '100%', display: 'flex', gap: '10px' }}>
            <button type="submit" style={btnStyle('#27ae60')}>Approve & Lock Budget</button>
            <button type="button" onClick={() => setShowApproveForm(false)} style={btnStyle('#95a5a6')}>Cancel</button>
          </div>
        </form>
      )}

      {!isMember && showDonationForm && (
        <form onSubmit={addDonation} style={{ ...formStyle, background: '#f9f0ff', borderColor: '#8e44ad' }}>
          <h3 style={{ margin: '0 0 15px', color: '#8e44ad', width: '100%' }}>Record Donation</h3>
          <div style={fieldStyle}><label style={labelStyle}>Donor Name</label><input value={donationForm.donor_name} onChange={e => setDonationForm({ ...donationForm, donor_name: e.target.value })} style={inputStyle} placeholder="Anonymous if blank" /></div>
          <div style={fieldStyle}><label style={labelStyle}>Amount (UGX) *</label><input type="number" value={donationForm.amount} onChange={e => setDonationForm({ ...donationForm, amount: e.target.value })} style={inputStyle} required /></div>
          <div style={fieldStyle}><label style={labelStyle}>Date *</label><input type="date" value={donationForm.donation_date} onChange={e => setDonationForm({ ...donationForm, donation_date: e.target.value })} style={inputStyle} required /></div>
          <div style={{ ...fieldStyle, minWidth: '300px' }}><label style={labelStyle}>Description</label><input value={donationForm.description} onChange={e => setDonationForm({ ...donationForm, description: e.target.value })} style={inputStyle} /></div>
          <div style={{ width: '100%', display: 'flex', gap: '10px' }}>
            <button type="submit" style={btnStyle('#8e44ad')}>Record Donation</button>
            <button type="button" onClick={() => setShowDonationForm(false)} style={btnStyle('#95a5a6')}>Cancel</button>
          </div>
        </form>
      )}

      {budgets.length > 1 && (
        <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', flexWrap: 'wrap' }}>
          {budgets.map(bg => (
            <button key={bg.id} onClick={() => loadBudget(bg.id)} style={{ padding: '6px 14px', border: 'none', cursor: 'pointer', fontSize: '13px', background: selectedBudget === bg.id ? '#2c3e50' : '#ecf0f1', color: selectedBudget === bg.id ? 'white' : '#2c3e50', borderRadius: '4px' }}>{bg.cycle_name}</button>
          ))}
        </div>
      )}

      {b && (
        <div style={{ display: 'flex', gap: '4px', marginBottom: '20px', borderBottom: '2px solid #ecf0f1' }}>
          {[
            { key: 'dashboard', label: '📊 Live Dashboard' },
            ...(!isMember ? [{ key: 'setup', label: '⚙ Budget Setup' }] : []),
            ...(!isMember ? [{ key: 'donations', label: `💰 Donations (${donations.length})` }] : []),
          ].map(t => (
            <button key={t.key} onClick={() => setTab(t.key)} style={{ padding: '8px 16px', border: 'none', cursor: 'pointer', fontSize: '13px', background: tab === t.key ? '#2c3e50' : '#ecf0f1', color: tab === t.key ? 'white' : '#2c3e50', borderRadius: '4px 4px 0 0' }}>{t.label}</button>
          ))}
        </div>
      )}

      {!b && !loading && (
        <div style={{ textAlign: 'center', padding: '60px', color: '#95a5a6' }}>
          <p style={{ fontSize: '16px' }}>No budget yet.</p>
          {!isMember && <button onClick={() => setShowNewForm(true)} style={btnStyle('#2c3e50')}>+ Create First Budget</button>}
        </div>
      )}
      {loading && <div style={{ textAlign: 'center', padding: '40px', color: '#95a5a6' }}>Loading...</div>}

      {/* ── LIVE DASHBOARD ── */}
      {b && tab === 'dashboard' && (
        <div>
          <div style={{ background: b.status === 'approved' ? '#eafaf1' : '#fef9e7', border: `1px solid ${b.status === 'approved' ? '#2ecc71' : '#f39c12'}`, borderRadius: '8px', padding: '12px 16px', marginBottom: '20px', fontSize: '13px' }}>
            <strong>{b.cycle_name}</strong> — {b.financial_year} —
            <span style={{ background: b.status === 'approved' ? '#2ecc71' : '#f39c12', color: 'white', padding: '2px 8px', borderRadius: '4px', fontSize: '11px', marginLeft: '8px' }}>{b.status.toUpperCase()}</span>
            {b.resolution_number && <span style={{ marginLeft: '12px', color: '#7f8c8d' }}>Resolution: {b.resolution_number} ({b.resolution_date?.slice(0, 10)})</span>}
          </div>
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '24px' }}>
            {[
              { label: 'Projected Income',     val: totalIncomeProjected, color: '#27ae60' },
              { label: 'Actual Income',         val: totalIncomeActual,    color: '#2ecc71' },
              { label: 'Projected Expenditure', val: totalExpProjected,    color: '#c0392b' },
              { label: 'Actual Expenditure',    val: totalExpActual,       color: '#e74c3c' },
              { label: 'Projected Surplus',     val: netProjected,         color: '#2c3e50' },
              { label: 'Actual Surplus',        val: netActual,            color: netActual >= 0 ? '#27ae60' : '#e74c3c' },
            ].map((c, i) => (
              <div key={i} style={{ background: c.color, color: 'white', padding: '14px 18px', borderRadius: '8px', minWidth: '160px', flex: '1' }}>
                <div style={{ fontSize: '11px', opacity: 0.85 }}>{c.label}</div>
                <div style={{ fontSize: '18px', fontWeight: 'bold', marginTop: '4px' }}>UGX {c.val.toLocaleString()}</div>
              </div>
            ))}
          </div>

          <div style={cardStyle}>
            <h3 style={{ ...sectionTitle, color: '#27ae60' }}>📈 Income</h3>
            <table style={tableStyle}>
              <thead><tr style={{ background: '#27ae60', color: 'white' }}><th style={thStyle}>Category</th><th style={{ ...thStyle, textAlign: 'right' }}>Projected</th><th style={{ ...thStyle, textAlign: 'right' }}>Actual</th><th style={{ ...thStyle, textAlign: 'right' }}>Variance</th><th style={thStyle}>Progress</th></tr></thead>
              <tbody>
                {incomeLines.map((l, i) => {
                  const actual = actuals?.income?.[l.category] || 0;
                  const proj   = parseFloat(l.projected_amount);
                  const pct    = proj > 0 ? Math.min(100, Math.round((actual / proj) * 100)) : 0;
                  return (
                    <tr key={l.id} style={{ background: i % 2 === 0 ? '#f9f9f9' : 'white' }}>
                      <td style={tdStyle}>{INCOME_LABELS[l.category] || l.category}</td>
                      <td style={{ ...tdStyle, textAlign: 'right' }}>{proj.toLocaleString()}</td>
                      <td style={{ ...tdStyle, textAlign: 'right' }}>{actual.toLocaleString()}</td>
                      <td style={{ ...tdStyle, textAlign: 'right' }}>{variance(proj, actual, 'income')}</td>
                      <td style={{ ...tdStyle, minWidth: '120px' }}><span style={{ fontSize: '11px', color: '#7f8c8d' }}>{pct}%</span>{progressBar(actual, proj)}</td>
                    </tr>
                  );
                })}
                <tr style={{ background: '#2c3e50', color: 'white', fontWeight: 'bold' }}>
                  <td style={tdStyle}>TOTAL</td>
                  <td style={{ ...tdStyle, textAlign: 'right' }}>{totalIncomeProjected.toLocaleString()}</td>
                  <td style={{ ...tdStyle, textAlign: 'right' }}>{totalIncomeActual.toLocaleString()}</td>
                  <td style={{ ...tdStyle, textAlign: 'right' }}>{variance(totalIncomeProjected, totalIncomeActual, 'income')}</td>
                  <td style={tdStyle}></td>
                </tr>
              </tbody>
            </table>
          </div>

          <div style={{ ...cardStyle, marginTop: '16px' }}>
            <h3 style={{ ...sectionTitle, color: '#e74c3c' }}>📉 Expenditure</h3>
            <table style={tableStyle}>
              <thead><tr style={{ background: '#e74c3c', color: 'white' }}><th style={thStyle}>Category</th><th style={{ ...thStyle, textAlign: 'right' }}>Projected</th><th style={{ ...thStyle, textAlign: 'right' }}>Actual</th><th style={{ ...thStyle, textAlign: 'right' }}>Variance (Savings)</th><th style={thStyle}>% Used</th></tr></thead>
              <tbody>
                {expLines.map((l, i) => {
                  const actual = actuals?.expenditure?.[l.category] || 0;
                  const proj   = parseFloat(l.projected_amount);
                  const pct    = proj > 0 ? Math.min(100, Math.round((actual / proj) * 100)) : 0;
                  return (
                    <tr key={l.id} style={{ background: i % 2 === 0 ? '#f9f9f9' : 'white' }}>
                      <td style={tdStyle}>{EXPENDITURE_LABELS[l.category] || l.category}</td>
                      <td style={{ ...tdStyle, textAlign: 'right' }}>{proj.toLocaleString()}</td>
                      <td style={{ ...tdStyle, textAlign: 'right' }}>{actual.toLocaleString()}</td>
                      <td style={{ ...tdStyle, textAlign: 'right' }}>{variance(proj, actual, 'expenditure')}</td>
                      <td style={{ ...tdStyle, minWidth: '120px' }}><span style={{ fontSize: '11px', color: '#7f8c8d' }}>{pct}%</span>{progressBar(actual, proj)}</td>
                    </tr>
                  );
                })}
                <tr style={{ background: '#2c3e50', color: 'white', fontWeight: 'bold' }}>
                  <td style={tdStyle}>TOTAL</td>
                  <td style={{ ...tdStyle, textAlign: 'right' }}>{totalExpProjected.toLocaleString()}</td>
                  <td style={{ ...tdStyle, textAlign: 'right' }}>{totalExpActual.toLocaleString()}</td>
                  <td style={{ ...tdStyle, textAlign: 'right' }}>{variance(totalExpProjected, totalExpActual, 'expenditure')}</td>
                  <td style={tdStyle}></td>
                </tr>
              </tbody>
            </table>
          </div>

          <div style={{ ...cardStyle, marginTop: '16px', display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
            <div style={{ flex: 1, textAlign: 'center', padding: '16px', background: '#f9f9f9', borderRadius: '6px' }}>
              <div style={{ fontSize: '12px', color: '#7f8c8d' }}>Projected Net Surplus / (Deficit)</div>
              <div style={{ fontSize: '24px', fontWeight: 'bold', color: netProjected >= 0 ? '#27ae60' : '#e74c3c', marginTop: '6px' }}>UGX {netProjected.toLocaleString()}</div>
            </div>
            <div style={{ flex: 1, textAlign: 'center', padding: '16px', background: '#f9f9f9', borderRadius: '6px' }}>
              <div style={{ fontSize: '12px', color: '#7f8c8d' }}>Actual Net Surplus / (Deficit)</div>
              <div style={{ fontSize: '24px', fontWeight: 'bold', color: netActual >= 0 ? '#27ae60' : '#e74c3c', marginTop: '6px' }}>UGX {netActual.toLocaleString()}</div>
            </div>
          </div>
        </div>
      )}

      {/* ── BUDGET SETUP ── */}
      {!isMember && b && tab === 'setup' && (
        <div>
          {b.status === 'approved' && (
            <div style={{ background: '#eafaf1', border: '1px solid #2ecc71', borderRadius: '6px', padding: '10px 16px', marginBottom: '16px', fontSize: '13px', color: '#27ae60' }}>
              ✓ Budget approved and locked. No further edits allowed.
            </div>
          )}
          <div style={cardStyle}>
            <h3 style={sectionTitle}>Income Projections</h3>
            <table style={tableStyle}>
              <thead><tr style={{ background: '#2c3e50', color: 'white' }}><th style={thStyle}>Category</th><th style={{ ...thStyle, textAlign: 'right' }}>Projected Amount (UGX)</th></tr></thead>
              <tbody>
                {incomeLines.map((l, i) => (
                  <tr key={l.id} style={{ background: i % 2 === 0 ? '#f9f9f9' : 'white' }}>
                    <td style={tdStyle}>{INCOME_LABELS[l.category] || l.category}</td>
                    <td style={{ ...tdStyle, textAlign: 'right' }}>
                      {b.status === 'approved' ? parseFloat(l.projected_amount).toLocaleString() : (
                        <input type="number" defaultValue={l.projected_amount} onBlur={e => updateLine(l.id, e.target.value)} style={{ ...inlineInput, width: '160px', textAlign: 'right' }} />
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div style={{ ...cardStyle, marginTop: '16px' }}>
            <h3 style={sectionTitle}>Expenditure Projections</h3>
            <table style={tableStyle}>
              <thead><tr style={{ background: '#2c3e50', color: 'white' }}><th style={thStyle}>Category</th><th style={{ ...thStyle, textAlign: 'right' }}>Projected Amount (UGX)</th></tr></thead>
              <tbody>
                {expLines.map((l, i) => (
                  <tr key={l.id} style={{ background: i % 2 === 0 ? '#f9f9f9' : 'white' }}>
                    <td style={tdStyle}>{EXPENDITURE_LABELS[l.category] || l.category}</td>
                    <td style={{ ...tdStyle, textAlign: 'right' }}>
                      {b.status === 'approved' ? parseFloat(l.projected_amount).toLocaleString() : (
                        <input type="number" defaultValue={l.projected_amount} onBlur={e => updateLine(l.id, e.target.value)} style={{ ...inlineInput, width: '160px', textAlign: 'right' }} />
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── DONATIONS ── */}
      {!isMember && b && tab === 'donations' && (
        <div style={cardStyle}>
          <h3 style={sectionTitle}>Donations Received</h3>
          <table style={tableStyle}>
            <thead><tr style={{ background: '#2c3e50', color: 'white' }}><th style={thStyle}>Date</th><th style={thStyle}>Donor</th><th style={{ ...thStyle, textAlign: 'right' }}>Amount (UGX)</th><th style={thStyle}>Description</th></tr></thead>
            <tbody>
              {donations.length === 0 && <tr><td colSpan="4" style={{ ...tdStyle, textAlign: 'center', color: '#95a5a6' }}>No donations recorded yet</td></tr>}
              {donations.map((d, i) => (
                <tr key={d.id} style={{ background: i % 2 === 0 ? '#f9f9f9' : 'white' }}>
                  <td style={tdStyle}>{d.donation_date?.slice(0, 10)}</td>
                  <td style={tdStyle}>{d.donor_name || 'Anonymous'}</td>
                  <td style={{ ...tdStyle, textAlign: 'right', color: '#27ae60', fontWeight: 'bold' }}>{parseFloat(d.amount).toLocaleString()}</td>
                  <td style={tdStyle}>{d.description || '—'}</td>
                </tr>
              ))}
              {donations.length > 0 && (
                <tr style={{ background: '#2c3e50', color: 'white', fontWeight: 'bold' }}>
                  <td style={tdStyle} colSpan="2">TOTAL</td>
                  <td style={{ ...tdStyle, textAlign: 'right' }}>{donations.reduce((s, d) => s + parseFloat(d.amount), 0).toLocaleString()}</td>
                  <td style={tdStyle}></td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const btnStyle     = (bg) => ({ background: bg, color: 'white', border: 'none', padding: '8px 16px', borderRadius: '4px', cursor: 'pointer', fontSize: '13px' });
const formStyle    = { background: '#f9f9f9', padding: '20px', borderRadius: '8px', marginBottom: '20px', display: 'flex', flexWrap: 'wrap', gap: '15px', border: '1px solid #ecf0f1' };
const cardStyle    = { background: 'white', border: '1px solid #ecf0f1', borderRadius: '8px', padding: '20px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' };
const sectionTitle = { margin: '0 0 16px', fontSize: '15px', fontWeight: 'bold' };
const fieldStyle   = { display: 'flex', flexDirection: 'column', gap: '5px', minWidth: '180px' };
const labelStyle   = { fontSize: '12px', color: '#7f8c8d', fontWeight: 'bold' };
const inputStyle   = { padding: '8px', borderRadius: '4px', border: '1px solid #ddd', fontSize: '14px' };
const inlineInput  = { padding: '6px 8px', borderRadius: '4px', border: '1px solid #ddd', fontSize: '13px' };
const tableStyle   = { width: '100%', borderCollapse: 'collapse' };
const thStyle      = { padding: '10px', textAlign: 'left', fontSize: '12px' };
const tdStyle      = { padding: '10px', borderBottom: '1px solid #eee', fontSize: '13px' };
const alertStyle   = (color) => ({ background: color, color: 'white', padding: '10px 15px', borderRadius: '6px', marginBottom: '15px' });

export default Projections;